/**
 * VFX5: photographic planet materials built on UV spheres (equirect maps map
 * cleanly onto SphereGeometry). Bodies with a manifest `tex` set use these;
 * untextured bodies keep their GLB mesh.
 *
 * Earth is the showcase: day baseColor + night-lights emissive (dark side only)
 * + a separate transparent cloud shell + a Rayleigh-ish atmosphere shell.
 */
import * as THREE from 'three';
import type { BodyTextureSet } from '@blackmarble/sim-core';
import {
  ATMO_VERT,
  ATMO_FRAG,
  ATMO_PRESETS,
  type AtmosphereBody,
} from './atmosphereShader.js';

const loader = new THREE.TextureLoader();

const loadSrgb = (url: string): THREE.Texture => {
  const t = loader.load(url);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};
const loadLinear = (url: string): THREE.Texture => {
  const t = loader.load(url);
  t.colorSpace = THREE.LinearSRGBColorSpace;
  return t;
};

const TEX_BASE = '/assets/bodies/';

/**
 * VFX5: Saturn's ring as a flat textured annulus (alpha PNG) in the equatorial
 * plane. Radial UVs map the ring strip from inner to outer edge.
 * @param ringPath path under public/assets/bodies/ (e.g. "tex/saturn_ring.png")
 */
export const buildRing = (planetRadiusKm: number, ringPath: string): THREE.Mesh => {
  const inner = planetRadiusKm * 1.2;
  const outer = planetRadiusKm * 2.3;
  const geo = new THREE.RingGeometry(inner, outer, 128);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    uv.setXY(i, (v.length() - inner) / (outer - inner), 0.5);
  }
  const tex = loadSrgb(TEX_BASE + ringPath);
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    alphaMap: tex,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    roughness: 1,
  });
  const ring = new THREE.Mesh(geo, mat);
  ring.rotation.x = Math.PI / 2;
  return ring;
};

/** A simple textured sphere for a generic body (gas giants, rocky planets). */
export const buildTexturedSphere = (radiusKm: number, tex: BodyTextureSet): THREE.Mesh => {
  const url = tex.lazy && tex.placeholder ? tex.placeholder : tex.day;
  const mat = new THREE.MeshStandardMaterial({ roughness: 0.95, metalness: 0 });
  if (url) mat.map = loadSrgb(TEX_BASE + url);
  // VFX7: rocky bodies regain relief via a normal map (gas giants have none).
  if (tex.normal) {
    mat.normalMap = loadLinear(TEX_BASE + tex.normal);
    mat.normalScale = new THREE.Vector2(0.8, 0.8);
  }
  // VFX9: TRUE geometric relief via a displacement map (lazy bodies get it on
  // the hi-res upgrade so the placeholder load stays light).
  const hasDisp = !!tex.displacement && !(tex.lazy && tex.placeholder);
  if (hasDisp && tex.displacement) {
    mat.displacementMap = loadLinear(TEX_BASE + tex.displacement);
    mat.displacementScale = tex.displacementScale ?? 0;
  }
  // Higher tessellation only where displacement needs it (perf-aware).
  const seg = tex.displacement ? 256 : 64;
  const geo = new THREE.SphereGeometry(radiusKm, seg, seg / 2);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.rotation.y = Math.PI; // align equirect seam to the back
  return mesh;
};

/** Swap a lazy body's placeholder map for the full-res day map + displacement. */
export const upgradeToHiRes = (mesh: THREE.Mesh, tex: BodyTextureSet): void => {
  const mat = mesh.material as THREE.MeshStandardMaterial;
  if (tex.day) {
    const full = loadSrgb(TEX_BASE + tex.day);
    full.onUpdate = () => undefined;
    mat.map = full;
  }
  // VFX9: attach the displacement map now (deferred from the placeholder build).
  if (tex.displacement && !mat.displacementMap) {
    mat.displacementMap = loadLinear(TEX_BASE + tex.displacement);
    mat.displacementScale = tex.displacementScale ?? 0;
  }
  mat.needsUpdate = true;
};


/**
 * Earth showcase: returns a group with surface + cloud + atmosphere layers.
 * Returns the cloud mesh too so the caller can rotate it slowly.
 */
export interface EarthBuild {
  group: THREE.Group;
  clouds?: THREE.Mesh;
  atmosphere: THREE.Mesh;
  atmosphereRadiusKm: number;
}

/** Earth surface material: day + night-lights + normal + specular. */
const earthSurfaceMat = (tex: BodyTextureSet): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ roughness: 0.7, metalness: 0.0 });
  if (tex.day) m.map = loadSrgb(TEX_BASE + tex.day);
  if (tex.night) {
    m.emissiveMap = loadSrgb(TEX_BASE + tex.night);
    m.emissive = new THREE.Color(0xffffaa);
    m.emissiveIntensity = 0.6;
  }
  if (tex.normal) {
    m.normalMap = loadLinear(TEX_BASE + tex.normal);
    m.normalScale = new THREE.Vector2(1.0, 1.0);
  }
  if (tex.specular) {
    m.roughnessMap = loadLinear(TEX_BASE + tex.specular);
    m.metalness = 0.1;
  }
  return m;
};

/** Transparent cloud shell at `radius` (luminance map as colour + alpha). */
const makeCloudShell = (radius: number, cloudsPath: string): THREE.Mesh => {
  const cTex = loadSrgb(TEX_BASE + cloudsPath);
  const mat = new THREE.MeshStandardMaterial({
    map: cTex,
    alphaMap: cTex,
    transparent: true,
    depthWrite: false,
    roughness: 1,
    metalness: 0,
  });
  const m = new THREE.Mesh(new THREE.SphereGeometry(radius, 96, 48), mat);
  m.rotation.y = Math.PI;
  return m;
};

/**
 * Sun-aware analytic-scattering atmosphere shell at `radius` (FrontSide, additive).
 * `body` selects the tint/optical-thickness preset (default Earth); Mars/Venus/Titan
 * presets are wired here so a future thin shell on those bodies is one call.
 */
const makeAtmosphere = (radius: number, body: AtmosphereBody = 'earth'): THREE.Mesh => {
  const p = ATMO_PRESETS[body];
  const mat = new THREE.ShaderMaterial({
    vertexShader: ATMO_VERT,
    fragmentShader: ATMO_FRAG,
    uniforms: {
      sunDir: { value: new THREE.Vector3(1, 0, 0) }, // updated per-frame in bodies.ts
      dayColor: { value: new THREE.Color(p.day[0], p.day[1], p.day[2]) },
      sunsetColor: { value: new THREE.Color(p.sunset[0], p.sunset[1], p.sunset[2]) },
      intensity: { value: p.intensity },
    },
    transparent: true,
    side: THREE.FrontSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(radius, 64, 32), mat);
};

/** Build a standalone thin atmosphere shell for a non-Earth body (Mars/Venus/Titan). */
export const buildBodyAtmosphere = (radiusKm: number, body: AtmosphereBody): THREE.Mesh =>
  makeAtmosphere(radiusKm * 1.02, body);

export const buildEarth = (radiusKm: number, tex: BodyTextureSet): EarthBuild => {
  const group = new THREE.Group();
  const surface = new THREE.Mesh(new THREE.SphereGeometry(radiusKm, 96, 48), earthSurfaceMat(tex));
  surface.rotation.y = Math.PI;
  group.add(surface);

  const clouds = tex.clouds ? makeCloudShell(radiusKm * 1.01, tex.clouds) : undefined;
  if (clouds) group.add(clouds);
  const atmoRadius = radiusKm * 1.025;
  const atmosphere = makeAtmosphere(atmoRadius);
  group.add(atmosphere);

  const build: EarthBuild = { group, atmosphere, atmosphereRadiusKm: atmoRadius };
  if (clouds) build.clouds = clouds;
  return build;
};

/**
 * BLENDER-1: Earth's cloud + atmosphere shells ONLY (no surface — that comes
 * from the sculpted GLB).
 *
 * BUBBLE FIX (dabc6067): `normaliseToRadius` already scales the sculpted GLB so
 * its mountain PEAKS sit AT radiusKm. So the shells must hug radiusKm directly —
 * the previous radiusKm*(1+maxDispFrac)*1.03 DOUBLE-COUNTED the displacement and
 * produced a giant soap-bubble with a gap. Peaks at radiusKm; clouds at 1.01x
 * (~64 km above peaks, not buried); atmosphere a thin rim at 1.02x.
 */
export const buildEarthShells = (radiusKm: number, tex: BodyTextureSet): EarthBuild => {
  const group = new THREE.Group();
  const clouds = tex.clouds ? makeCloudShell(radiusKm * 1.01, tex.clouds) : undefined;
  if (clouds) group.add(clouds);
  const atmoR = radiusKm * 1.02;
  const atmosphere = makeAtmosphere(atmoR);
  group.add(atmosphere);

  const build: EarthBuild = { group, atmosphere, atmosphereRadiusKm: atmoR };
  if (clouds) build.clouds = clouds;
  return build;
};
